// src/slices/ticketType/reducer.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  TicketType,
  TicketTypeFilters,
  TicketTypePagination,
  TicketTypeModalState,
} from 'pages/TicketTypeManagement/types';
import {
  fetchTicketTypes,
  fetchActiveTicketTypes,
  createTicketType,
  updateTicketType,
  lockTicketType,
  unlockTicketType,
} from './thunk';

interface TicketTypeState {
  ticketTypes: TicketType[];
  activeTicketTypes: TicketType[];
  selectedTicketType: TicketType | null;
  pagination: TicketTypePagination;
  filters: TicketTypeFilters;
  loading: boolean;
  activeLoading: boolean;
  submitting: boolean;
  error: string | null;
  modalState: TicketTypeModalState;
}

const initialState: TicketTypeState = {
  ticketTypes: [],
  activeTicketTypes: [],
  selectedTicketType: null,
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalItems: 0,
    totalPages: 0,
  },
  filters: {
    searchTerm: '',
    isLocked: 'All',
  },
  loading: false,
  activeLoading: false,
  submitting: false,
  error: null,
  modalState: {
    create: false,
    edit: false,
  },
};

const ticketTypeSlice = createSlice({
  name: 'TicketType',
  initialState,
  reducers: {
    setSelectedTicketType: (state, action: PayloadAction<TicketType | null>) => {
      state.selectedTicketType = action.payload;
    },
    // Hydrate tu cache (localStorage) thay vi goi API — dung boi loadActiveTicketTypesWithCache
    setActiveTicketTypes: (state, action: PayloadAction<TicketType[]>) => {
      state.activeTicketTypes = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
      state.pagination.currentPage = 1;
    },
    setFilters: (state, action: PayloadAction<Partial<TicketTypeFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = { searchTerm: '', isLocked: 'All' };
      state.pagination.currentPage = 1;
    },
    openModal: (state, action: PayloadAction<keyof TicketTypeModalState>) => {
      state.modalState[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<keyof TicketTypeModalState>) => {
      state.modalState[action.payload] = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch danh sách (paginated, cho màn quản trị)
      .addCase(fetchTicketTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTicketTypes.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.ticketTypes = payload.items || payload.data || payload || [];
        state.pagination.totalItems = payload.totalItems ?? payload.total ?? state.ticketTypes.length;
        state.pagination.totalPages = payload.totalPages ?? Math.ceil(state.pagination.totalItems / state.pagination.pageSize);
      })
      .addCase(fetchTicketTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch danh sách active (cho dropdown tạo kế hoạch phát hành)
      .addCase(fetchActiveTicketTypes.pending, (state) => {
        state.activeLoading = true;
      })
      .addCase(fetchActiveTicketTypes.fulfilled, (state, action) => {
        state.activeLoading = false;
        const payload = action.payload as any;
        state.activeTicketTypes = payload.items || payload.data || payload || [];
      })
      .addCase(fetchActiveTicketTypes.rejected, (state) => {
        state.activeLoading = false;
      })

      // Tạo mới
      .addCase(createTicketType.pending, (state) => {
        state.submitting = true;
      })
      .addCase(createTicketType.fulfilled, (state) => {
        state.submitting = false;
        state.modalState.create = false;
      })
      .addCase(createTicketType.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      // Cập nhật
      .addCase(updateTicketType.pending, (state) => {
        state.submitting = true;
      })
      .addCase(updateTicketType.fulfilled, (state) => {
        state.submitting = false;
        state.modalState.edit = false;
        state.selectedTicketType = null;
      })
      .addCase(updateTicketType.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      // Khóa
      .addCase(lockTicketType.pending, (state) => {
        state.submitting = true;
      })
      .addCase(lockTicketType.fulfilled, (state, action) => {
        state.submitting = false;
        const index = state.ticketTypes.findIndex(t => t.id === action.payload);
        if (index !== -1) state.ticketTypes[index].isLocked = true;
      })
      .addCase(lockTicketType.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      // Mở khóa
      .addCase(unlockTicketType.pending, (state) => {
        state.submitting = true;
      })
      .addCase(unlockTicketType.fulfilled, (state, action) => {
        state.submitting = false;
        const index = state.ticketTypes.findIndex(t => t.id === action.payload);
        if (index !== -1) state.ticketTypes[index].isLocked = false;
      })
      .addCase(unlockTicketType.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedTicketType,
  setActiveTicketTypes,
  setError,
  clearError,
  setPage,
  setPageSize,
  setFilters,
  clearFilters,
  openModal,
  closeModal,
} = ticketTypeSlice.actions;

export default ticketTypeSlice.reducer;
